/**
 * Dashboard Widget Registry
 *
 * Single source of truth for all available dashboard widgets.
 * The grid uses 4 columns on desktop (lg), 2 on tablet (md), 1 on mobile (sm).
 * Each cell is 160px tall with a 24px gap. Widgets occupy whole cells only.
 *
 * Default layout (4-column desktop):
 *   Row 0:   [stat-total-revenue][stat-total-brands][stat-active-deals][stat-completion-rate]
 *   Row 1-2: [chart-deal-status (2×2)][stat-pipeline-value][stat-awaiting-payment]
 *   Row 2:   [chart-deal-status cont.][stat-pending-deliverables][stat-overdue-deliverables]
 *
 * Adding a new widget requires:
 * 1. Add definition here with defaultX/Y/W/H
 * 2. Add rendering logic in DraggableWidget component
 * 3. Add type to WidgetType union in shared package
 */

import type { WidgetDefinition, WidgetLayout } from '@creatoros/shared';

/**
 * Default widget definitions
 */
export const WIDGET_REGISTRY: WidgetDefinition[] = [
  // ── Stat widgets (1×1 cells) ──────────────────────────────────────────────
  {
    id: 'stat-total-revenue',
    type: 'stat-total-revenue',
    label: 'Total Revenue',
    description: 'Your total revenue from completed deals',
    defaultX: 0,
    defaultY: 0,
    defaultW: 1,
    defaultH: 1,
    category: 'stats',
    configurable: false,
    removable: true,
  },
  {
    id: 'stat-total-brands',
    type: 'stat-total-brands',
    label: 'Total Brands',
    description: 'Number of brand partnerships',
    defaultX: 1,
    defaultY: 0,
    defaultW: 1,
    defaultH: 1,
    category: 'stats',
    configurable: false,
    removable: true,
  },
  {
    id: 'stat-active-deals',
    type: 'stat-active-deals',
    label: 'Active Deals',
    description: 'Number of deals currently in progress',
    defaultX: 2,
    defaultY: 0,
    defaultW: 1,
    defaultH: 1,
    category: 'stats',
    configurable: false,
    removable: true,
  },
  {
    id: 'stat-completion-rate',
    type: 'stat-completion-rate',
    label: 'Completion Rate',
    description: 'Percentage of deliverables completed',
    defaultX: 3,
    defaultY: 0,
    defaultW: 1,
    defaultH: 1,
    category: 'stats',
    configurable: false,
    removable: true,
  },
  {
    id: 'stat-pipeline-value',
    type: 'stat-pipeline-value',
    label: 'Pipeline Value',
    description: 'Total value of deals in progress',
    defaultX: 2,
    defaultY: 1,
    defaultW: 1,
    defaultH: 1,
    category: 'stats',
    configurable: false,
    removable: true,
  },
  {
    id: 'stat-awaiting-payment',
    type: 'stat-awaiting-payment',
    label: 'Awaiting Payment',
    description: 'Deals waiting for payment',
    defaultX: 3,
    defaultY: 1,
    defaultW: 1,
    defaultH: 1,
    category: 'stats',
    configurable: false,
    removable: true,
  },
  {
    id: 'stat-pending-deliverables',
    type: 'stat-pending-deliverables',
    label: 'Pending Deliverables',
    description: 'Number of deliverables awaiting completion',
    defaultX: 2,
    defaultY: 2,
    defaultW: 1,
    defaultH: 1,
    category: 'stats',
    configurable: false,
    removable: true,
  },
  {
    id: 'stat-overdue-deliverables',
    type: 'stat-overdue-deliverables',
    label: 'Overdue Deliverables',
    description: 'Number of deliverables past their due date',
    defaultX: 3,
    defaultY: 2,
    defaultW: 1,
    defaultH: 1,
    category: 'stats',
    configurable: false,
    removable: true,
  },

  // ── Chart widgets (2×2 cells) ─────────────────────────────────────────────
  {
    id: 'chart-deal-status',
    type: 'chart-deal-status',
    label: 'Deal Status Breakdown',
    description: 'Visual breakdown of your deal pipeline',
    defaultX: 0,
    defaultY: 1,
    defaultW: 2,
    defaultH: 2,
    category: 'charts',
    configurable: false,
    removable: true,
  },
  {
    id: 'chart-revenue-by-period',
    type: 'chart-revenue-by-period',
    label: 'Revenue By Period',
    description: 'Revenue earned over time based on selected period',
    defaultX: 0,
    defaultY: 3,
    defaultW: 2,
    defaultH: 2,
    category: 'charts',
    configurable: false,
    removable: true,
  },
  {
    id: 'chart-deliverables-by-platform',
    type: 'chart-deliverables-by-platform',
    label: 'Deliverables by Platform',
    description: 'Content distribution by social platform',
    defaultX: 2,
    defaultY: 3,
    defaultW: 2,
    defaultH: 2,
    category: 'charts',
    configurable: false,
    removable: true,
  },
  {
    id: 'chart-deliverables-by-status',
    type: 'chart-deliverables-by-status',
    label: 'Deliverables by Status',
    description: 'Content status distribution',
    defaultX: 0,
    defaultY: 5,
    defaultW: 2,
    defaultH: 2,
    category: 'charts',
    configurable: false,
    removable: true,
  },

  // ── List widgets (2×2 cells) ──────────────────────────────────────────────
  {
    id: 'list-top-brands',
    type: 'list-top-brands',
    label: 'Top Brands',
    description: 'Your highest-value brand partnerships',
    defaultX: 2,
    defaultY: 5,
    defaultW: 2,
    defaultH: 2,
    category: 'lists',
    configurable: false,
    removable: true,
  },
  {
    id: 'list-recent-activity',
    type: 'list-recent-activity',
    label: 'Recent Activity',
    description: 'Latest actions in your account',
    defaultX: 0,
    defaultY: 7,
    defaultW: 2,
    defaultH: 2,
    category: 'lists',
    configurable: false,
    removable: true,
  },

  // ── Full-width list widgets (4×2 cells) ───────────────────────────────────
  {
    id: 'deadlines-upcoming',
    type: 'deadlines-upcoming',
    label: 'Upcoming Deadlines',
    description: 'Content, invoices, and proposals due soon',
    defaultX: 0,
    defaultY: 9,
    defaultW: 4,
    defaultH: 2,
    category: 'lists',
    configurable: false,
    removable: true,
  },
];

/**
 * IDs of widgets shown by default. All others start hidden and can be
 * added via the widget selector.
 */
export const DEFAULT_VISIBLE_WIDGET_IDS = new Set([
  'stat-total-revenue',
  'stat-total-brands',
  'stat-active-deals',
  'stat-completion-rate',
  'chart-deal-status',
  'stat-pipeline-value',
  'stat-awaiting-payment',
  'stat-pending-deliverables',
  'stat-overdue-deliverables',
]);

/**
 * Get default layout for the 4-column desktop grid.
 * Only the DEFAULT_VISIBLE_WIDGET_IDS widgets are visible; others are hidden
 * and available to add via the widget selector.
 */
export const getDefaultLayout = (): WidgetLayout[] => {
  return WIDGET_REGISTRY.map((def) => ({
    id: def.id,
    type: def.type,
    x: def.defaultX,
    y: def.defaultY,
    w: def.defaultW,
    h: def.defaultH,
    visible: DEFAULT_VISIBLE_WIDGET_IDS.has(def.id),
  }));
};

/**
 * Find widget definition by ID
 */
export const getWidgetDefinition = (id: string): WidgetDefinition | undefined => {
  return WIDGET_REGISTRY.find((w) => w.id === id);
};

/**
 * Get all widgets by category
 */
export const getWidgetsByCategory = (
  category: 'stats' | 'charts' | 'lists'
): WidgetDefinition[] => {
  return WIDGET_REGISTRY.filter((w) => w.category === category);
};
